import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Headset,
  PhoneOutgoing,
  Users,
  CalendarClock,
  Wrench,
  Zap,
  TrendingUp,
  Scaling,
  SlidersHorizontal,
  Check,
  MessageSquare,
  PhoneCall,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";


const services = [
  {
    title: "Inbound Customer Support",
    description: "Handle customer inquiries, complaints, and support requests with trained, professional agents.",
    Icon: Headset,
    color: "text-amber",
  },
  {
    title: "Outbound Sales Campaigns",
    description: "Drive revenue with proactive outbound campaigns run by experienced sales agents.",
    Icon: PhoneOutgoing,
    color: "text-sky",
  },
  {
    title: "Lead Generation",
    description: "Identify and qualify prospects to build a stronger sales pipeline.",
    Icon: Users,
    color: "text-lime",
  },
  {
    title: "Appointment Setting",
    description: "Schedule and confirm appointments on your behalf, reducing no-shows and admin overhead.",
    Icon: CalendarClock,
    color: "text-violet",
  },
  {
    title: "Technical Assistance",
    description: "Provide first-line technical support to resolve customer issues quickly.",
    Icon: Wrench,
    color: "text-navy",
  },
];

const benefits = [
  {
    title: "Faster Response Times",
    description: "Trained agents handle interactions quickly and professionally.",
    Icon: Zap,
    gradient: "icon-tile-teal",
  },
  {
    title: "Higher Conversion Rates",
    description: "Skilled outbound teams turn more conversations into results.",
    Icon: TrendingUp,
    gradient: "icon-tile-indigo",
  },
  {
    title: "Scalable Support",
    description: "Scale your call center capacity up or down as your business needs change.",
    Icon: Scaling,
    gradient: "icon-tile-blue",
  },
  {
    title: "Custom Workflows",
    description: "Tailored scripts and processes built around your specific business needs.",
    Icon: SlidersHorizontal,
    gradient: "icon-tile-orange",
  },
];

const industries = [
  "Banks & Financial Institutions",
  "Fintech & Payment Platforms",
  "Retail & Ecommerce",
  "Telecom & MNOs",
  "Enterprise SaaS Platforms",
];

const channels = [
  {
    title: "Voice",
    description: "Professional inbound and outbound call handling.",
    Icon: PhoneCall,
    gradient: "icon-tile-teal",
  },
  {
    title: "SMS",
    description: "Follow-up confirmations and appointment reminders after a call.",
    Icon: MessageSquare,
    gradient: "icon-tile-indigo",
  },
];

const faqs = [
  {
    question: "What is a call center solution?",
    answer: "A call center solution is a managed service that handles customer communications — such as inbound support, outbound sales calls, lead generation, or appointment scheduling — using trained agents and telecom infrastructure.",
  },
  {
    question: "What types of call center services does Yootelco offer?",
    answer: "Yootelco offers inbound customer support, outbound sales campaigns, lead generation, appointment setting, and technical assistance, tailored to your business needs.",
  },
  {
    question: "Can call center services scale with my business?",
    answer: "Yes, Yootelco's call center solutions are designed to scale up or down based on your call volume and business needs.",
  },
  {
    question: "How are agents trained?",
    answer: "Agents are trained on your specific products, services, and customer communication standards to ensure professional, results-driven interactions.",
  },
  {
    question: "Can call center solutions integrate with our existing systems?",
    answer: "Yes, Yootelco can build custom workflows that integrate with your existing CRM and business systems.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const SolutionCallCenter = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="min-h-screen bg-white">
      <Seo
        title="Call Center Solutions: Inbound & Outbound | Yootelco"
        description="Scalable call center solutions for inbound support, outbound sales, and lead generation, with results-driven customer communications."
        path="/solutions/call-center-solutions"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Professional, Scalable Customer Communications
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              Call Center Solutions
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Reliable, scalable call center solutions tailored to your business needs — from inbound support to outbound sales campaigns. Yootelco's experienced team helps you optimize every customer interaction.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Why Businesses Choose Yootelco */}
      <section className="section-shell pt-[20px] md:pt-[40px]">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Why Businesses Choose Yootelco for Call Center Solutions</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            With more than 18 years of experience in customer communications, we provide reliable and scalable call center solutions tailored to your business needs. Whether you require inbound customer support, outbound sales campaigns, lead generation, appointment setting, or technical assistance, our experienced team delivers professional, results-driven service. We help businesses optimize customer interactions, improve response times, and increase conversion rates through highly trained agents, advanced technology, and customized workflows.
          </p>
        </div>
      </section>

      {/* Services We Offer */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">Services We Offer</h2>
        </div>
        <div className="mx-auto mt-[32px] flex max-w-[1120px] flex-wrap items-stretch justify-center gap-4">
          {services.map((item) => (
            <article
              key={item.title}
              className="w-full rounded-[16px] border border-border/70 bg-secondary/40 p-[24px] sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)]"
            >
              <FlatIcon Icon={item.Icon} color={item.color} />
              <h3 className="mt-[14px] text-[16px] font-bold text-foreground">{item.title}</h3>
              <p className="mt-[8px] text-[13px] leading-[1.55] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Key Benefits */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">Key Benefits</h2>
        </div>
        <div className="mx-auto mt-[32px] flex max-w-[1080px] flex-wrap justify-center gap-5">
          {benefits.map((item) => (
            <article
              key={item.title}
              className="w-full rounded-[16px] border border-border/70 bg-secondary/40 p-[24px] text-center sm:w-[calc(50%-0.625rem)] lg:w-[calc(25%-0.94rem)]"
            >
              <span className={`mx-auto flex h-11 w-11 items-center justify-center rounded-[12px] ${item.gradient}`}>
                <item.Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="mt-[14px] text-[17px] font-bold text-foreground">{item.title}</h3>
              <p className="mx-auto mt-[8px] max-w-[260px] text-[13px] leading-[1.55] text-muted-foreground">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px]">
          <h2 className="text-[28px] font-bold leading-[1.2] tracking-tight text-foreground md:text-[32px]">Industries We Serve</h2>
          <ul className="mt-[18px] grid gap-[14px] sm:grid-cols-2">
            {industries.map((item) => (
              <li key={item} className="flex items-start gap-3 text-[14.5px] leading-[1.55] text-foreground">
                <span className="mt-[2px] flex h-[20px] w-[20px] flex-none items-center justify-center rounded-full bg-primary/10">
                  <Check className="h-3 w-3 text-primary" strokeWidth={3} />
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Supported Channels */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">Supported Channels</h2>
        </div>
        <div className="mx-auto mt-[32px] flex max-w-[1120px] flex-wrap items-stretch justify-center gap-4">
          {channels.map((item) => (
            <article
              key={item.title}
              className="flex h-full w-full items-start gap-3 rounded-[14px] border border-border/70 bg-secondary/40 px-[18px] py-[16px] sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)]"
            >
              <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-[10px] ${item.gradient}`}>
                <item.Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="text-[14px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[4px] text-[12.5px] leading-[1.5] text-muted-foreground">{item.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA Banner */}
            <CTASection
        title="Talk to Sales"
        description="Tell us about your customer support or sales campaign needs – we'll show you how Yootelco's call center solutions can help."
        useCase="Call Center Solutions"
      />

      {/* FAQ */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[800px] rounded-[36px] border border-border/70 bg-gradient-to-br from-secondary/60 to-card px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <h2 className="section-title">Ready to Optimize Your Customer Communications?</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
            Talk to our team about call center solutions tailored to your business.
          </p>
          <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default SolutionCallCenter;
